import { useState } from 'react';

import { Accordion, AccordionButton, AccordionItem, AccordionPanel, Flex } from '@chakra-ui/react';
import { ChevronLeft, Icon } from '@mesh.ai/rabbit';
import { getApplicableL2MyDataModules } from 'Deer/components/navigation/secondary/ModuleSection';
import { useRouteToNavLevels } from 'Deer/hooks/useRouteToL3';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { Link, useHistory, useRouteMatch } from 'react-router-dom';

import { NetchexEngagement, NetchexMinus, NetchexPerformance, NetchexPlus } from 'assets/assetsV1/iconComponents';

import { isMobileView } from 'constants/isMobileView';
import { EMPLOYEE_VIEW_MODULE_INTENTS_MAP, netchexRoutes } from 'constants/netChexConstants';

import { TextBodyLarge, TextBodyMedium } from 'uiToolkitV1/Typography';

// this matches both path and query params as many intents have same path but different query params
const useRouteAndQueryParamMatch = ({ intentPath, exact }) => {
  const history = useHistory();
  const { search: addressBarSearch = '' } = history.location;

  const addressBarURLParams = new URLSearchParams(addressBarSearch);

  const [intentPathname, intentPathQueryParams] = intentPath.split('?');
  const intentPathURLParams = new URLSearchParams(intentPathQueryParams);

  // check if intentPathURLParams matches addressBarURLParams, there could be additional params in addressBarURLParams, which we don't care about
  const isQueryParamsMatch = [...intentPathURLParams.entries()].every(([key, value]) => {
    const addressBarValue = addressBarURLParams.get(key);
    return addressBarValue === value;
  });

  const match = useRouteMatch({
    path: intentPathname,
    exact,
  });

  return match && isQueryParamsMatch;
};

const NavigationLink = ({ intentPath, label, onNavigationClick }) => {
  const isActiveRoute = useRouteAndQueryParamMatch({ intentPath, exact: false });
  return (
    <Link to={intentPath} onClick={onNavigationClick}>
      <Flex
        style={{
          background: isActiveRoute ? '#EFF6FF' : 'transparent',
          borderLeft: isActiveRoute ? '5px solid #3B82F6' : '5px solid transparent',
          height: '35px',
          borderTopLeftRadius: '3px',
          borderBottomLeftRadius: '3px',
          alignItems: 'center',
        }}
      >
        <TextBodyMedium
          style={{
            marginLeft: '24px',
          }}
        >
          {label}
        </TextBodyMedium>
      </Flex>
    </Link>
  );
};

const getModuleKeyLabelMap = (applicableModules) => {
  const moduleKeyLabelMap = {};
  applicableModules.forEach((module) => {
    moduleKeyLabelMap[module.key] = module.label;
  });
  return moduleKeyLabelMap;
};

const AccordionNavigation = ({
  isMobileNav,
  onNavigationClick = () => {},
}: {
  isMobileNav?: boolean;
  onNavigationClick?: () => void;
}) => {
  const { l2Module } = useRouteToNavLevels();
  const { moduleName } = useOrganisationSettings();
  const isMobileViewBool = isMobileView();

  const applicableL2Modules = getApplicableL2MyDataModules(moduleName);
  const applicableModuleKeyLabelMap = getModuleKeyLabelMap(applicableL2Modules);

  const currentModuleIntents = (EMPLOYEE_VIEW_MODULE_INTENTS_MAP[l2Module] ?? []).filter(({ isMobileSupport }) => {
    if (isMobileViewBool) {
      return isMobileSupport;
    }
    return true;
  });

  const [openAccordions, setOpenAccordions] = useState(0);

  // if l2Module is of type engagement then parent module is Engagement else Performance
  const parentModule = l2Module === 'engagement' ? 'Engagement' : 'Performance';
  return (
    <>
      {!isMobileNav && (
        <>
          <Flex p="20px 12px" pb="10px" pl="4px" color="#2563EB" alignItems="center">
            <a
              href={netchexRoutes.dashboard}
              style={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Icon size="large" IconSVG={ChevronLeft} />
              <span>Back to My Dashboard</span>
            </a>
          </Flex>
          <Flex p="10px 12px" alignItems="center">
            <Icon
              style={{
                marginRight: '8px',
                color: 'transparent',
                fill: 'transparent',
                height: '20px',
                width: '20px',
              }}
              IconSVG={l2Module === 'engagement' ? NetchexEngagement : NetchexPerformance}
            />
            <TextBodyLarge>{parentModule}</TextBodyLarge>
          </Flex>
        </>
      )}
      <Accordion index={openAccordions} allowToggle onChange={(index) => setOpenAccordions(index)}>
        <AccordionItem>
          <AccordionButton
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <TextBodyLarge>{applicableModuleKeyLabelMap[l2Module]}</TextBodyLarge>
            <Icon
              style={{
                height: '12px',
                width: '12px',
              }}
              IconSVG={openAccordions === 0 ? NetchexMinus : NetchexPlus}
            />
          </AccordionButton>

          <AccordionPanel style={{ padding: '0px' }}>
            {currentModuleIntents.map(({ key, label, path }) => (
              <NavigationLink onNavigationClick={onNavigationClick} label={label} intentPath={path} key={key} />
            ))}
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </>
  );
};

export { AccordionNavigation };
